// src/core/LeaderboardUI.js

import LeaderboardClient from './LeaderboardClient.js';
import PlayerIdentity from './PlayerIdentity.js';

class LeaderboardUI {
    constructor() {
        this.overlay = null;
    }

    /**
     * Mounts the leaderboard permanently in the game view.
     * Desktop CSS docks it to the side; portrait/mobile stacks it below the game.
     * @param {string} gameId - Game identifier
     */
    async mount(gameId) {
        const topScores = await LeaderboardClient.getTopScores(gameId, 10);
        this._render({ topScores });
    }

    /**
     * Called when a game ends. Submits score and refreshes the always-visible leaderboard.
     * @param {string} gameId - Game identifier
     * @param {number} score - Final score
     * @param {function} onClose - Kept for API compatibility; no longer rendered as a leaderboard button.
     */
    async show(gameId, score, onClose) {
        const playerName = PlayerIdentity.getName() || 'Anonymous';

        // Submit first, then fetch (so the leaderboard includes the new score)
        const submitResult = await LeaderboardClient.submit(gameId, playerName, score);
        const topScores = await LeaderboardClient.getTopScores(gameId, 10);

        this._render({
            topScores,
            playerName,
            score,
            rank: submitResult?.rank,
        });

        // The play/restart control belongs to the game UI, not the leaderboard.
        const restartButton = document.getElementById('restart-btn-2d');
        if (restartButton) {
            restartButton.style.display = 'block';
        }
    }

    hide() {
        if (this.overlay) {
            this.overlay.remove();
            this.overlay = null;
        }

        const appContainer = document.getElementById('app-container');
        if (appContainer) {
            appContainer.classList.remove('leaderboard-active');
        }
    }

    _render({ topScores, playerName = PlayerIdentity.getName() || '', score = null, rank = null }) {
        if (this.overlay) {
            this.overlay.remove();
        }

        const appContainer = document.getElementById('app-container');
        const leaderboardSlot = document.getElementById('leaderboard-slot');
        const gameContainer = document.getElementById('game-container');
        const target = leaderboardSlot || gameContainer || document.body;

        this.overlay = document.createElement('div');
        this.overlay.className = 'leaderboard-overlay leaderboard-docked';

        const rankText = rank ? `#${rank}` : '';
        const isNewBest = rank && rank <= 3;
        const scoreBlock = score !== null ? `
            <div class="leaderboard-score-display">
                <span class="leaderboard-your-score">${score}</span>
                ${rankText ? `<span class="leaderboard-rank ${isNewBest ? 'leaderboard-rank-top' : ''}">${rankText}</span>` : ''}
            </div>
        ` : '';

        this.overlay.innerHTML = `
            <div class="leaderboard-panel">
                <div class="leaderboard-header">
                    ${scoreBlock}
                </div>
                <div class="leaderboard-list">
                    ${this._renderScores(topScores, playerName)}
                </div>
            </div>
        `;

        if (leaderboardSlot && appContainer) {
            appContainer.classList.add('leaderboard-active');
        }

        target.appendChild(this.overlay);

        // Animate in
        requestAnimationFrame(() => this.overlay.classList.add('leaderboard-visible'));
    }

    _renderScores(scores, currentPlayer) {
        if (!scores.length) {
            return '<div class="leaderboard-empty">No scores yet — be the first!</div>';
        }

        return scores.map((entry, i) => {
            const position = i + 1;
            const medal = position === 1 ? '🥇' : position === 2 ? '🥈' : position === 3 ? '🥉' : `${position}.`;
            const isYou = currentPlayer && entry.name === currentPlayer;
            return `
                <div class="leaderboard-row ${isYou ? 'leaderboard-row-you' : ''}">
                    <span class="leaderboard-position">${medal}</span>
                    <span class="leaderboard-name">${entry.name}${isYou ? ' (you)' : ''}</span>
                    <span class="leaderboard-row-score">${entry.score}</span>
                </div>
            `;
        }).join('');
    }
}

export default LeaderboardUI;
